/**
 * Top-level container that routes between the SIM wizard, details, and result views.
 *
 * @module components/sim/takeSim/TakeSim
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React from 'react';
import { Grid } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import {useLocation} from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import SimWizard from './SimWizard.jsx';
import TakeSimDetails from './TakeSimDetails.jsx';
import TakeSimResult from './TakeSimResult.jsx';
import {simWizard} from './SimWizard.js';
import config from '../../../config';
import {clearAudioStorage} from "../../../utils/audioFetch";

/**
 * TakeSim is a React functional component designed to manage and render the user interface of
 * a simulation application. It handles simulation details, video recording support, and user
 * interaction workflows based on specific environment configurations and application states.
 *
 * @param {Object} props - The component's input properties.
 * @param {Array.<string>} props.simProductType - Specifies the type of simulation product. It influences
 *        the rendering of internal data-related components.
 *
 * @returns {JSX.Element} Renders different components and layouts based on the user's interaction state,
 *          operating system support, and the current simulation workflow.
 *
 * Component Behavior:
 * - Tracks the current view state of the simulation (`viewState`) to toggle between detail view and result view.
 * - Manages the user's interaction or simulation results (`userResult`) and determines if video recording
 *   is a valid feature to enable (`hasVideo`).
 * - Checks the operating system to disable unsupported features like video recording when on MacOS (`invalidOs`).
 * - Responds to simulation workflow results provided by the `location` state and renders
 *   the appropriate output.
 *
 * Additional Details:
 * - Utilizes Material-UI for theming (`makeStyles`) and layout management (e.g., `Grid` components).
 * - Includes conditional rendering logic to represent two main scenarios based on the `simProductType`
 *   and `viewState`.
 * - Renders `TakeSimDetails`, `SimWizard`, or `TakeSimResult` components based on the user's interaction
 *   phase and results.
 * - Displays a progress spinner when the simulation state is undefined or loading.
 */
const TakeSim = ({ simProductType }) => {
    const location = useLocation();
    const useStyles = makeStyles((theme) => simWizard(theme));
    const classes = useStyles();

    const [viewState, setViewState] = React.useState(false);
    const [userResult, setUserResult] = React.useState({});
    const [hasVideo, setHasVideo] = React.useState(false);

    React.useEffect(
        () => {
            if (!viewState) {
                clearAudioStorage().then(() => {

                });
            }
        },
        [viewState]
    );

    /**
     * Updates the current view state based on the provided view.
     *
     * @function handleViewState
     * @param {string} view - The view to set as the current state.
     */
    const handleViewState = (view) => {
        setViewState(view);
    };

    if (location.state.viewResult === true) {
        localStorage.setItem('sims_id', localStorage.getItem('sim_id'));
        return <TakeSimResult isPost={false}
                              config={config}
                              userResult={userResult}
                              setUserResult={setUserResult}
                              hasVideo={hasVideo}
                              setHasVideo={setHasVideo}
        />;
    }

    if (!viewState) {
        return (
            <>
                {simProductType.includes('Internal Data') ? (
                    <Grid container spacing={1}>
                        <Grid item xs={8}>
                            <TakeSimDetails userResponse={userResult}/>
                        </Grid>
                        <Grid item xs={4} className={classes.takesimGrid}>
                            <SimWizard handleViewState={handleViewState}
                                       config={config}
                                       userResult={userResult}
                                       setUserResult={setUserResult}
                                       hasVideo={hasVideo}
                                       setHasVideo={setHasVideo}
                            />
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={1}>
                        <Grid item xs={8} style={{ margin: 'auto' }}>
                            <SimWizard handleViewState={handleViewState}
                                       config={config}
                                       userResult={userResult}
                                       setUserResult={setUserResult}
                                       hasVideo={hasVideo}
                                       setHasVideo={setHasVideo}
                            />
                        </Grid>
                    </Grid>
                )}
            </>
        );
    }

    if (viewState === true) {
        return <TakeSimResult isPost
                              config={config}
                              userResult={userResult}
                              setUserResult={setUserResult}
                              hasVideo={hasVideo}
                              setHasVideo={setHasVideo}
        />;
    }
    return (
        <div>
            <CircularProgress color="secondary" />
        </div>
    );
};

TakeSim.propTypes = {
    simProductType: PropTypes.string,
};

const mapStateToProps = (state) => ({
    simProductType: state.sim.currentSimProductType,
    userResult: state.sim.userResult,
});

export default connect(mapStateToProps)(TakeSim);
